/* ----------------------------------------------------------------------------
   PRODUCT GALLERY — vertical-scroll → horizontal navigation

   Port of the live theme (Dawn SliderComponent.onScrollTransformation): while
   the pointer is over the product media gallery, a vertical wheel/trackpad
   gesture scrolls the gallery horizontally. Snap is disabled during the gesture
   so movement tracks the pointer 1:1 (smooth, not stepped), then the gallery
   settles on the nearest item once the wheel goes quiet. At either end the
   gesture is released so the page scrolls normally.

   Snap is suppressed with a CLASS, not an inline style: ScrollCarousel's own
   scrollend handler calls style.removeProperty('scroll-snap-type'), which would
   re-enable mandatory snap mid-gesture and pull the gallery back.
---------------------------------------------------------------------------- */
(function () {
  const SELECTOR = 'scroll-carousel.product-gallery__carousel';
  const PANNING_CLASS = 'is-wheel-panning';
  const SETTLE_MS = 140;

  function nearestIndex(carousel) {
    const cells = carousel.cells || [];
    const target = carousel.scrollLeft + carousel.clientWidth / 2;
    let best = 0;
    let bestDistance = Infinity;

    cells.forEach((cell, index) => {
      const distance = Math.abs(cell.offsetLeft + cell.clientWidth / 2 - target);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = index;
      }
    });

    return best;
  }

  function attach(carousel) {
    if (!carousel || carousel.dataset.wheelNav === '1') return;
    carousel.dataset.wheelNav = '1';

    let paused = false;
    let direction = 0;
    let settleTimer = null;

    carousel.addEventListener(
      'wheel',
      (event) => {
        // The custom element must be upgraded (theme.js loaded) to expose its API.
        if (typeof carousel.select !== 'function') return;

        const maxScroll = carousel.scrollWidth - carousel.clientWidth;
        if (maxScroll <= 1) return; // nothing to scroll (e.g. grid layout, single image)

        // Vertical and horizontal deltas both drive horizontal movement (as live does).
        const distance = event.deltaX + event.deltaY;
        if (distance > -1 && distance < 1) return;

        // Reversing direction re-arms after an end-of-gallery pause.
        const currentDirection = distance > 0 ? 1 : -1;
        if (currentDirection !== direction) paused = false;
        direction = currentDirection;
        if (paused) return;

        // At the ends, let the gesture through so the page scrolls normally.
        if (
          (distance > 0 && carousel.scrollLeft >= maxScroll - 1) ||
          (distance < 0 && carousel.scrollLeft <= 0)
        ) {
          paused = true;
          carousel.classList.remove(PANNING_CLASS);
          return;
        }

        event.preventDefault();

        carousel.classList.add(PANNING_CLASS);
        carousel.scrollLeft += distance;

        clearTimeout(settleTimer);
        settleTimer = setTimeout(() => {
          const index = nearestIndex(carousel);
          carousel.classList.remove(PANNING_CLASS);
          carousel.select(index);
        }, SETTLE_MS);
      },
      { passive: false }
    );
  }

  function scan(root) {
    (root || document).querySelectorAll(SELECTOR).forEach(attach);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan(document));
  } else {
    scan(document);
  }

  // Quick-buy modal (and theme-editor section loads) inject galleries later.
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.matches && node.matches(SELECTOR)) attach(node);
        if (node.querySelectorAll) scan(node);
      }
    }
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
